/* eslint-disable id-length */

import { useLayoutEffect, useMemo, useState } from "react";
import {
  type Edge,
  type Node,
  useEdgesState,
  useNodesInitialized,
  useNodesState,
  useStore,
} from "reactflow";
import { parse, toGraph } from "app/lib/pipeline";
import Dagre from "@dagrejs/dagre";
import { markerEndForEdge } from "./Edge";

const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));

/**
 * Layout nodes and edges using Dagre
 *
 * @see https://github.com/dagrejs/dagre
 */
const layoutElements = (nodes: Node[], edges: Edge[]) => {
  if (!nodes.length) {
    return { nodes, edges };
  }

  g.setGraph({ rankdir: "LR", ranksep: 100 });

  edges.forEach((edge) => g.setEdge(edge.source, edge.target));

  nodes.forEach((node) => {
    return g.setNode(node.id, {
      width: node.width || undefined,
      height: node.height || undefined,
    });
  });

  Dagre.layout(g);

  return {
    nodes: nodes.map((node: Node) => {
      const { x, y, width, height } = g.node(node.id);

      return {
        ...node,
        position: {
          x: x - width / 2,
          y: y - height / 2,
        },
      };
    }),
    edges: edges.map((edge: Edge) => {
      return {
        ...edge,
        markerEnd: markerEndForEdge(edge),
      };
    }),
  };
};

/**
 * Parse YAML and transform it into a graph
 */
const useGraph = (yaml?: string) => {
  return useMemo(() => {
    if (!yaml) {
      return { nodes: [], edges: [] };
    }

    return toGraph(parse(yaml));
  }, [yaml]);
};

/**
 * Transform YAML into a graph and layout the elements
 */
export default function usePlaygroundState(yaml?: string) {
  const { edges: initialEdges, nodes: initialNodes } = useGraph(yaml);
  const [nodes, setNodes] = useNodesState(initialNodes);
  const [edges, setEdges] = useEdgesState(initialEdges);
  const nodeInternals = useStore((state) => state.nodeInternals);
  const nodesInitialized = useNodesInitialized();
  const [positioned, setPositioned] = useState(false);

  useLayoutEffect(() => {
    setEdges(initialEdges);
    setNodes(initialNodes);
  }, [initialEdges, initialNodes]);

  useLayoutEffect(() => {
    if (!nodesInitialized) {
      return;
    }

    const { nodes: newNodes, edges: newEdges } = layoutElements(
      Array.from(nodeInternals.values()),
      edges,
    );

    setNodes(newNodes);
    setEdges(newEdges);
    setPositioned(true);
  }, [nodesInitialized]);

  return { nodes, edges, positioned };
}
